import { BASE_URL } from "../config";
import { getSessionId } from "../utils/SessionHandler";

/**
 * Centralized API helper for making HTTP requests
 * Reduces code duplication across API modules
 */

const DEFAULT_HEADERS = {
  "Content-Type": "application/json",
};

/**
 * Get authorization headers with bearer token
 * @returns {Object} Headers object with authorization
 */
const getAuthHeaders = () => ({
  ...DEFAULT_HEADERS,
  Authorization: `Bearer ${getSessionId()}`,
});

/**
 * Handle API response consistently
 * @param {Response} response - Fetch response object
 * @returns {Promise<Object>} Parsed JSON response
 */
export const handleResponse = async (response) => {
  if (!response.ok) {
    const contentType = response.headers.get("content-type");
    if (contentType?.includes("application/json")) {
      return response.json();
    }
    return {
      error: {
        description: "Something went wrong! It has been reported to our admins.",
      },
    };
  }
  return response.json();
};

/**
 * Make a GET request
 * @param {string} endpoint - API endpoint (without base URL)
 * @param {Object} options - Additional options
 * @param {boolean} options.auth - Whether to include auth headers (default: true)
 * @returns {Promise<Object>} API response
 */
export const get = async (endpoint, { auth = true, noCache = false } = {}) => {
  const headers = auth ? getAuthHeaders() : { ...DEFAULT_HEADERS };
  const response = await fetch(`${BASE_URL}${endpoint}`, {
    method: "GET",
    headers,
    ...(noCache && { cache: "no-store" }),
  });
  return handleResponse(response);
};

/**
 * Make a POST request
 * @param {string} endpoint - API endpoint (without base URL)
 * @param {Object} data - Request body data
 * @param {Object} options - Additional options
 * @param {boolean} options.auth - Whether to include auth headers (default: true)
 * @returns {Promise<Object>} API response
 */
export const post = async (endpoint, data = null, { auth = true } = {}) => {
  const config = {
    method: "POST",
    headers: auth ? getAuthHeaders() : DEFAULT_HEADERS,
  };

  if (data !== null) {
    config.body = JSON.stringify(data);
  }

  const response = await fetch(`${BASE_URL}${endpoint}`, config);
  return handleResponse(response);
};

/**
 * Make a PATCH request
 * @param {string} endpoint - API endpoint (without base URL)
 * @param {Object} data - Request body data
 * @param {Object} options - Additional options
 * @param {boolean} options.auth - Whether to include auth headers (default: true)
 * @returns {Promise<Object>} API response
 */
export const patch = async (endpoint, data, { auth = true } = {}) => {
  const response = await fetch(`${BASE_URL}${endpoint}`, {
    method: "PATCH",
    headers: auth ? getAuthHeaders() : DEFAULT_HEADERS,
    body: JSON.stringify(data),
  });
  return handleResponse(response);
};

/**
 * Make a DELETE request
 * @param {string} endpoint - API endpoint (without base URL)
 * @param {Object} data - Optional request body data
 * @param {Object} options - Additional options
 * @param {boolean} options.auth - Whether to include auth headers (default: true)
 * @returns {Promise<Object>} API response
 */
export const del = async (endpoint, data = null, { auth = true } = {}) => {
  const config = {
    method: "DELETE",
    headers: auth ? getAuthHeaders() : DEFAULT_HEADERS,
  };

  if (data !== null) {
    config.body = JSON.stringify(data);
  }

  const response = await fetch(`${BASE_URL}${endpoint}`, config);
  return handleResponse(response);
};

// Re-export for backward compatibility
export { handleResponse as default };
